import { useState, useCallback, useMemo } from 'react'
import { AdminLayout } from '~/components/AdminLayout'
import { 
  usePricingSettings, 
  useUpdatePricing, 
  useCurrentCommission,
  useCommissionHistory,
  useCreateCommission,
  useDriverEarnings,
  type PricingSettings,
} from '~/api/settings'
import 'remixicon/fonts/remixicon.css'
import styles from './settings.module.css'

interface StoreSettings {
  storeName: string
  storeEmail: string
  storePhone: string
  storeAddress: string
  currency: string
  timezone: string
}

interface DeliverySettings {
  minOrderAmount: number
  deliveryFee: number
  freeDeliveryThreshold: number
  maxDeliveryRadius: number
  estimatedDeliveryTime: string
}

interface NotificationSettings {
  orderConfirmation: boolean
  orderStatusUpdates: boolean
  deliveryAlerts: boolean
  marketingEmails: boolean
}

const defaultStoreSettings: StoreSettings = {
  storeName: 'Tasamago',
  storeEmail: 'info@tasamago.com',
  storePhone: '+62 21 1234 5678',
  storeAddress: 'Jakarta, Indonesia',
  currency: 'IDR',
  timezone: 'Asia/Jakarta',
}

const defaultDeliverySettings: DeliverySettings = {
  minOrderAmount: 50000,
  deliveryFee: 15000,
  freeDeliveryThreshold: 200000,
  maxDeliveryRadius: 25,
  estimatedDeliveryTime: '30-60',
}

const defaultNotificationSettings: NotificationSettings = {
  orderConfirmation: true,
  orderStatusUpdates: true,
  deliveryAlerts: true,
  marketingEmails: false,
}

interface PricingFormData {
  smallPrice: number
  mediumPrice: number
  largePrice: number
  packSize: number
  packDiscountPercent: number
}

const defaultPricingForm: PricingFormData = {
  smallPrice: 2500,
  mediumPrice: 3000,
  largePrice: 3500,
  packSize: 30,
  packDiscountPercent: 0,
}

function pricingFormFromSettings(pricingSettings: PricingSettings): PricingFormData {
  return {
    smallPrice: pricingSettings.eggPrices.small,
    mediumPrice: pricingSettings.eggPrices.medium,
    largePrice: pricingSettings.eggPrices.large,
    packSize: pricingSettings.packSize,
    packDiscountPercent: pricingSettings.packDiscountPercent,
  }
}

export function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState<'store' | 'delivery' | 'notifications' | 'pricing' | 'commission'>('store')
  const [storeSettings, setStoreSettings] = useState<StoreSettings>(defaultStoreSettings)
  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings>(defaultDeliverySettings)
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(defaultNotificationSettings)
  const [pricingFormOverride, setPricingFormOverride] = useState<PricingFormData | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [newCommissionAmount, setNewCommissionAmount] = useState<number>(0)
  
  // Fetch pricing settings from API
  const { data: pricingSettings, isLoading: isPricingLoading } = usePricingSettings()
  const updatePricing = useUpdatePricing()
  
  // Commission hooks
  const { data: currentCommission, isLoading: isCommissionLoading } = useCurrentCommission()
  const { data: commissionHistory } = useCommissionHistory()
  const createCommission = useCreateCommission()
  
  // Driver earnings - default to current month
  const earningsPeriod = useMemo(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
    return {
      start: start.toISOString(),
      end: end.toISOString(),
    }
  }, [])
  const { data: driverEarnings, isLoading: isEarningsLoading } = useDriverEarnings(
    earningsPeriod.start,
    earningsPeriod.end
  )
  
  const pricingDefaults = useMemo(
    () => (pricingSettings ? pricingFormFromSettings(pricingSettings) : defaultPricingForm),
    [pricingSettings]
  )
  const pricingForm = pricingFormOverride === null ? pricingDefaults : pricingFormOverride

  const handleSave = useCallback(async () => {
    setSaveStatus('saving')
    
    if (activeTab === 'pricing') {
      try {
        await updatePricing.mutateAsync({
          smallPrice: pricingForm.smallPrice,
          mediumPrice: pricingForm.mediumPrice,
          largePrice: pricingForm.largePrice,
          packSize: pricingForm.packSize,
          packDiscountPercent: pricingForm.packDiscountPercent,
        })
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus('idle'), 2000)
      } catch {
        setSaveStatus('idle')
      }
    } else {
      // Simulate save for other tabs - in production this would call an API
      setTimeout(() => {
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus('idle'), 2000)
      }, 500)
    }
  }, [activeTab, pricingForm, updatePricing])

  const handleStoreChange = useCallback((field: keyof StoreSettings, value: string) => {
    setStoreSettings(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleDeliveryChange = useCallback((field: keyof DeliverySettings, value: string | number) => {
    setDeliverySettings(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleNotificationChange = useCallback((field: keyof NotificationSettings, value: boolean) => {
    setNotificationSettings(prev => ({ ...prev, [field]: value }))
  }, [])

  const handlePricingChange = useCallback((field: keyof PricingFormData, value: number) => {
    setPricingFormOverride(prev => {
      const current = prev === null ? pricingForm : prev
      return { ...current, [field]: value }
    })
  }, [pricingForm])

  return (
    <AdminLayout title="SETTINGS">
      <div className={styles.settingsPage}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'store' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('store')}
          >
            <i className="ri-store-2-line" />
            STORE
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'delivery' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('delivery')}
          >
            <i className="ri-truck-line" />
            DELIVERY
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'notifications' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <i className="ri-notification-3-line" />
            NOTIFICATIONS
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'pricing' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('pricing')}
          >
            <i className="ri-price-tag-3-line" />
            PRICING
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'commission' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('commission')}
          >
            <i className="ri-money-dollar-circle-line" />
            COMMISSION
          </button>
        </div>

        {/* Store Settings */}
        {activeTab === 'store' && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>STORE INFORMATION</h3>
            <div className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Store Name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={storeSettings.storeName}
                  onChange={(e) => handleStoreChange('storeName', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email</label>
                <input
                  type="email"
                  className={styles.input}
                  value={storeSettings.storeEmail}
                  onChange={(e) => handleStoreChange('storeEmail', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone</label>
                <input
                  type="text"
                  className={styles.input}
                  value={storeSettings.storePhone}
                  onChange={(e) => handleStoreChange('storePhone', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Address</label>
                <textarea
                  className={styles.textarea}
                  value={storeSettings.storeAddress}
                  onChange={(e) => handleStoreChange('storeAddress', e.target.value)}
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Currency</label>
                  <select
                    className={styles.select}
                    value={storeSettings.currency}
                    onChange={(e) => handleStoreChange('currency', e.target.value)}
                  >
                    <option value="IDR">IDR - Indonesian Rupiah</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="SGD">SGD - Singapore Dollar</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Timezone</label>
                  <select
                    className={styles.select}
                    value={storeSettings.timezone}
                    onChange={(e) => handleStoreChange('timezone', e.target.value)}
                  >
                    <option value="Asia/Jakarta">Asia/Jakarta (WIB)</option>
                    <option value="Asia/Makassar">Asia/Makassar (WITA)</option>
                    <option value="Asia/Jayapura">Asia/Jayapura (WIT)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delivery Settings */}
        {activeTab === 'delivery' && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>DELIVERY CONFIGURATION</h3>
            <div className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Minimum Order Amount (IDR)</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={deliverySettings.minOrderAmount}
                    onChange={(e) => handleDeliveryChange('minOrderAmount', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Delivery Fee (IDR)</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={deliverySettings.deliveryFee}
                    onChange={(e) => handleDeliveryChange('deliveryFee', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Free Delivery Threshold (IDR)</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={deliverySettings.freeDeliveryThreshold}
                    onChange={(e) => handleDeliveryChange('freeDeliveryThreshold', parseInt(e.target.value) || 0)}
                  />
                  <span className={styles.hint}>Orders above this amount get free delivery</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Max Delivery Radius (km)</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={deliverySettings.maxDeliveryRadius}
                    onChange={(e) => handleDeliveryChange('maxDeliveryRadius', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Estimated Delivery Time (minutes)</label>
                <input
                  type="text"
                  className={styles.input}
                  value={deliverySettings.estimatedDeliveryTime}
                  onChange={(e) => handleDeliveryChange('estimatedDeliveryTime', e.target.value)}
                  placeholder="e.g., 30-60"
                />
              </div>
            </div>
          </div>
        )}

        {/* Notification Settings */}
        {activeTab === 'notifications' && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>NOTIFICATION PREFERENCES</h3>
            <div className={styles.form}>
              <div className={styles.toggleGroup}>
                <label className={styles.toggleLabel}>
                  <span>Order Confirmation</span>
                  <span className={styles.toggleHint}>Send confirmation email when order is placed</span>
                </label>
                <button
                  className={`${styles.toggle} ${notificationSettings.orderConfirmation ? styles.toggleOn : ''}`}
                  onClick={() => handleNotificationChange('orderConfirmation', !notificationSettings.orderConfirmation)}
                  aria-label="Toggle order confirmation"
                >
                  <span className={styles.toggleKnob} />
                </button>
              </div>
              <div className={styles.toggleGroup}>
                <label className={styles.toggleLabel}>
                  <span>Order Status Updates</span>
                  <span className={styles.toggleHint}>Notify customers when order status changes</span>
                </label>
                <button
                  className={`${styles.toggle} ${notificationSettings.orderStatusUpdates ? styles.toggleOn : ''}`}
                  onClick={() => handleNotificationChange('orderStatusUpdates', !notificationSettings.orderStatusUpdates)}
                  aria-label="Toggle order status updates"
                >
                  <span className={styles.toggleKnob} />
                </button>
              </div>
              <div className={styles.toggleGroup}>
                <label className={styles.toggleLabel}>
                  <span>Delivery Alerts</span>
                  <span className={styles.toggleHint}>Send SMS/push when driver is nearby</span>
                </label>
                <button
                  className={`${styles.toggle} ${notificationSettings.deliveryAlerts ? styles.toggleOn : ''}`}
                  onClick={() => handleNotificationChange('deliveryAlerts', !notificationSettings.deliveryAlerts)}
                  aria-label="Toggle delivery alerts"
                >
                  <span className={styles.toggleKnob} />
                </button>
              </div>
              <div className={styles.toggleGroup}>
                <label className={styles.toggleLabel}>
                  <span>Marketing Emails</span>
                  <span className={styles.toggleHint}>Send promotional offers and newsletters</span>
                </label>
                <button
                  className={`${styles.toggle} ${notificationSettings.marketingEmails ? styles.toggleOn : ''}`}
                  onClick={() => handleNotificationChange('marketingEmails', !notificationSettings.marketingEmails)}
                  aria-label="Toggle marketing emails"
                >
                  <span className={styles.toggleKnob} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Settings */}
        {activeTab === 'pricing' && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>EGG PRICING (IDR)</h3>
            {isPricingLoading ? (
              <div className={styles.loadingText}>Loading pricing settings...</div>
            ) : (
              <div className={styles.form}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Small Egg Price</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={pricingForm.smallPrice}
                      onChange={(e) => handlePricingChange('smallPrice', parseInt(e.target.value) || 0)}
                      min={0}
                    />
                    <span className={styles.hint}>Price per small egg</span>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Medium Egg Price</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={pricingForm.mediumPrice}
                      onChange={(e) => handlePricingChange('mediumPrice', parseInt(e.target.value) || 0)}
                      min={0}
                    />
                    <span className={styles.hint}>Price per medium egg</span>
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Large Egg Price</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={pricingForm.largePrice}
                      onChange={(e) => handlePricingChange('largePrice', parseInt(e.target.value) || 0)}
                      min={0}
                    />
                    <span className={styles.hint}>Price per large egg</span>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Pack Size</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={pricingForm.packSize}
                      onChange={(e) => handlePricingChange('packSize', parseInt(e.target.value) || 0)}
                      min={1}
                    />
                    <span className={styles.hint}>Number of eggs per pack</span>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Pack Discount (%)</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={pricingForm.packDiscountPercent}
                    onChange={(e) => handlePricingChange('packDiscountPercent', parseInt(e.target.value) || 0)}
                    min={0}
                    max={100}
                  />
                  <span className={styles.hint}>Discount percentage for full pack purchases</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Commission Settings */}
        {activeTab === 'commission' && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>DRIVER COMMISSION</h3>
            {isCommissionLoading ? (
              <div className={styles.loadingText}>Loading commission settings...</div>
            ) : (
              <div className={styles.form}>
                {/* Current Rate */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>Current Rate (IDR per order)</label>
                  <div style={{ 
                    padding: '16px', 
                    background: 'var(--color-background)', 
                    border: '2px solid var(--color-accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-accent)' }}>
                      Rp {currentCommission?.amountPerOrder?.toLocaleString() ?? 0}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-muted-foreground)' }}>
                      {currentCommission?.effectiveFrom 
                        ? `Effective since ${new Date(currentCommission.effectiveFrom).toLocaleDateString()}`
                        : 'No rate set'}
                    </span>
                  </div>
                </div>

                {/* Set New Rate */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>New Commission Rate</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={newCommissionAmount || ''}
                      onChange={(e) => setNewCommissionAmount(parseInt(e.target.value) || 0)}
                      min={0}
                      placeholder="Enter amount in IDR"
                    />
                    <span className={styles.hint}>Commission paid per delivered order</span>
                  </div>
                  <div className={styles.formGroup} style={{ justifyContent: 'flex-end' }}>
                    <button
                      className={styles.saveButton}
                      onClick={async () => {
                        if (newCommissionAmount > 0) {
                          await createCommission.mutateAsync({ amountPerOrder: newCommissionAmount })
                          setNewCommissionAmount(0)
                        }
                      }}
                      disabled={createCommission.isPending || newCommissionAmount <= 0}
                      style={{ marginTop: 'auto' }}
                    >
                      {createCommission.isPending ? (
                        <><i className="ri-loader-4-line ri-spin" /> UPDATING...</>
                      ) : (
                        <><i className="ri-check-line" /> SET NEW RATE</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Rate History */}
                {commissionHistory && commissionHistory.length > 0 && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Rate History</label>
                    <div style={{ 
                      background: 'var(--color-background)', 
                      border: '1px solid var(--color-border)',
                      maxHeight: '150px',
                      overflow: 'auto'
                    }}>
                      {commissionHistory.slice(0, 5).map((item, idx) => (
                        <div 
                          key={item.id} 
                          style={{ 
                            padding: '10px 16px', 
                            borderBottom: idx < commissionHistory.length - 1 ? '1px solid var(--color-border)' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>Rp {item.amountPerOrder.toLocaleString()}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--color-muted-foreground)' }}>
                            {new Date(item.effectiveFrom).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Driver Earnings This Month */}
            <h3 className={styles.sectionTitle} style={{ marginTop: '32px' }}>
              DRIVER EARNINGS — {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}
            </h3>
            {isEarningsLoading ? (
              <div className={styles.loadingText}>Loading earnings...</div>
            ) : driverEarnings && driverEarnings.length > 0 ? (
              <div style={{ 
                background: 'var(--color-background)', 
                border: '1px solid var(--color-border)',
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 100px 120px', 
                  padding: '12px 16px',
                  borderBottom: '2px solid var(--color-border)',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  color: 'var(--color-muted-foreground)'
                }}>
                  <span>DRIVER</span>
                  <span style={{ textAlign: 'right' }}>DELIVERIES</span>
                  <span style={{ textAlign: 'right' }}>COMMISSION</span>
                </div>
                {driverEarnings.map((driver) => (
                  <div 
                    key={driver.driverId} 
                    style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 100px 120px', 
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--color-border)',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{driver.driverName}</span>
                    <span style={{ textAlign: 'right', color: 'var(--color-muted-foreground)' }}>
                      {driver.totalDeliveries}
                    </span>
                    <span style={{ textAlign: 'right', fontWeight: 700, color: 'var(--color-accent)' }}>
                      Rp {driver.totalCommission.toLocaleString()}
                    </span>
                  </div>
                ))}
                {/* Total Row */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 100px 120px', 
                  padding: '12px 16px',
                  background: 'var(--color-muted)',
                  fontWeight: 700
                }}>
                  <span>TOTAL</span>
                  <span style={{ textAlign: 'right' }}>
                    {driverEarnings.reduce((sum, d) => sum + d.totalDeliveries, 0)}
                  </span>
                  <span style={{ textAlign: 'right', color: 'var(--color-accent)' }}>
                    Rp {driverEarnings.reduce((sum, d) => sum + d.totalCommission, 0).toLocaleString()}
                  </span>
                </div>
              </div>
            ) : (
              <div className={styles.loadingText}>No deliveries this month</div>
            )}
          </div>
        )}

        {/* Save Button */}
        <div className={styles.actions}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
          >
            {saveStatus === 'saving' && <i className="ri-loader-4-line ri-spin" />}
            {saveStatus === 'saved' && <i className="ri-check-line" />}
            {saveStatus === 'idle' && <i className="ri-save-line" />}
            {saveStatus === 'saving' ? 'SAVING...' : saveStatus === 'saved' ? 'SAVED' : 'SAVE CHANGES'}
          </button>
        </div>
      </div>
    </AdminLayout>
  )
}
